import React, {useState} from 'react'
import Page, {rootEventBreadcrumbs} from 'organization/Event/Page'
import {useEvent, useUpdate} from 'Event/EventProvider'
import {useOrganization} from 'organization/OrganizationProvider'
import {routesWithValue} from 'lib/url'
import {useBreadcrumbs} from 'lib/ui/BreadcrumbProvider'
import Form, {UpdateEventData} from 'organization/Event/EventSettings/Form'
import {useFileSelect} from 'lib/ui/form/file'
import {useValidatedForm} from 'lib/form'
import {EventBreadcrumbs} from 'organization/Event/Page/PageBreadcrumbs'
import {usePlanLimits} from 'obvio/PlanLimitsProvider'

export default function UpdateEventForm() {
  const [submitting, setSubmitting] = useState(false)
  const {event} = useEvent()
  const {routes: organizationRoutes} = useOrganization()
  const {checkPlanLimits} = usePlanLimits()

  const [hasEndDateTimeChange, setHasEndDateTimeChange] = useState(false)
  const {
    register,
    errors,
    handleSubmit,
    control,
    responseError,
    setResponseError,
    watch,
    setValue,
  } = useValidatedForm()

  const {set: setBreadcrumbs} = useBreadcrumbs()
  const update = useUpdate()

  const favicon = useFileSelect(event.favicon)
  const logo = useFileSelect(event.logo)

  const data = (form: UpdateEventData) => {
    const formData = new FormData()
    for (const [key, value] of Object.entries(form)) {
      // Skip `null` values, as FormData sends them as
      // strings,
      if (value === null) {
        continue
      }

      formData.set(key, String(value))
    }

    if (favicon.selected) {
      formData.set('favicon', favicon.selected)
    }

    if (favicon.wasRemoved) {
      formData.set('favicon', '')
    }

    if (logo.selected) {
      formData.set('logo', logo.selected)
    }

    if (logo.wasRemoved) {
      formData.set('logo', '')
    }

    return formData
  }

  const submit = (form: UpdateEventData) => {
    if (submitting) {
      return
    }

    setSubmitting(true)
    setResponseError(null)

    update(data(form))
      .then((updated) => {
        const routes = routesWithValue(
          ':event',
          updated.slug,
          organizationRoutes.events[':event'],
        )

        setBreadcrumbs(rootEventBreadcrumbs(routes, updated, 'Event'))
        // Update slug in URL without triggering reload
        window.history.replaceState(null, '', routes.root)
      })
      .catch((error) => {
        setSubmitting(false)
        checkPlanLimits(error)
        setResponseError(error)
      })
      .finally(() => {
        setSubmitting(false)
        setHasEndDateTimeChange(false)
      })
  }

  return (
    <EventBreadcrumbs page="Event Settings">
      <Page>
        <Form
          onSubmit={handleSubmit(submit)}
          register={register}
          errors={errors}
          responseError={responseError}
          slug={watch('slug')}
          systemDomainId={watch('system_domain_id')}
          submitLabel="Save"
          submitting={submitting}
          control={control}
          favicon={favicon}
          logo={logo}
          setValue={setValue}
          hasEndDateTimeChange={hasEndDateTimeChange}
          setHasEndDateTimeChange={setHasEndDateTimeChange}
        />
      </Page>
    </EventBreadcrumbs>
  )
}
